/**
 * Centralized company slug resolution helper
 * 
 * CRITICAL: URL slug is ALWAYS the source of truth when present.
 * Priority: URL > localStorage v2 (only on company routes) > user data > environment-based default
 * 
 * ROUTE-AWARE BEHAVIOR:
 * - On company routes (/:company/...): Extract slug from URL, save to localStorage
 * - On superadmin routes (/superadmin/...): Ignore localStorage, return undefined
 * - On auth/public routes (/login, /, etc.): Ignore localStorage, return undefined
 * - On logout/switch: Clean up localStorage slug
 * 
 * This ensures that if a user opens /superadmin/..., 
 * the app does NOT reuse old company slug from localStorage.
 */

import { isSuperadminPath } from './routeHelpers';

/**
 * Known public routes that should NOT be treated as company slugs
 */
const PUBLIC_ROUTES = [
  'login',
  'superadmin',
  'about',
  'terms',
  'privacy',
  'register',
  'leadsregister',
  'features',
  'pricing',
  'contact',
  'crm-for-small-business',
  'crm-appointments',
  'oauth',
  'lp',
  'undefined',
  'auth',
  'public',
];

/**
 * Check if a pathname is a superadmin route
 */
function isSuperadminRoute(pathname: string): boolean {
  return isSuperadminPath(pathname);
}

/**
 * Check if a pathname is an auth or public route
 */
function isAuthOrPublicPath(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/superadmin/login') ||
    pathname.startsWith('/register') ||
    pathname === '/leadsregister' ||
    pathname.startsWith('/leadsregister/') ||
    pathname.startsWith('/features') ||
    pathname.startsWith('/pricing') ||
    pathname.startsWith('/about') ||
    pathname.startsWith('/contact')
  );
}

/**
 * Check if a pathname is a company route (requires company slug)
 */
function isCompanyRoute(pathname: string): boolean {
  // Exclude superadmin and auth/public routes
  if (isSuperadminRoute(pathname) || isAuthOrPublicPath(pathname)) {
    return false;
  }
  
  // Company routes have format /:company/...
  const parts = pathname.split('/').filter(Boolean);
  return parts.length >= 1 && parts[0] !== 'superadmin';
}

/**
 * Determine runtime environment from hostname
 * 
 * @returns 'local' | 'staging' | 'production'
 */
export function getRuntimeEnvFromHostname(): 'local' | 'staging' | 'production' {
  if (typeof window === 'undefined') {
    // SSR - default to production for safety
    return 'production';
  }

  const hostname = window.location.hostname.toLowerCase();

  // Local development
  if (hostname.includes('localhost') || 
      hostname.includes('127.0.0.1') || 
      hostname.includes('0.0.0.0') ||
      hostname === '') {
    return 'local';
  }

  // Staging/dev environments
  if (hostname.includes('dev') || 
      hostname.includes('staging') || 
      hostname.includes('test') && !hostname.includes('crmtest.cloud')) {
    return 'staging';
  }

  // Production (crmtest.cloud, etc.)
  return 'production';
}

/**
 * Extract company slug from current URL location
 * 
 * Reads window.location.pathname and extracts the first path segment
 * as a potential company slug, ignoring known public routes.
 * 
 * @returns Company slug from URL, or null if not found/invalid
 */
export function getCompanySlugFromLocation(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const pathname = window.location.pathname;
  
  // CRITICAL: If on superadmin or auth/public route, return null immediately
  if (isSuperadminRoute(pathname) || isAuthOrPublicPath(pathname)) {
    return null;
  }
  
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length === 0) {
    return null;
  }

  const firstSegment = parts[0];

  // CRITICAL: Never treat "superadmin" as a company slug
  if (firstSegment === 'superadmin' || firstSegment.toLowerCase() === 'superadmin') {
    return null;
  }

  // Ignore public routes
  if (PUBLIC_ROUTES.includes(firstSegment.toLowerCase())) {
    return null;
  }

  // Ignore invalid values
  if (firstSegment === 'undefined' || 
      firstSegment === 'null' || 
      firstSegment.trim() === '') {
    return null;
  }

  // Basic validation: alphanumeric, underscore, hyphen
  // Must be at least 1 character, max 100 characters
  if (!/^[a-zA-Z0-9_-]+$/.test(firstSegment) || 
      firstSegment.length < 1 || 
      firstSegment.length > 100) {
    return null;
  }

  return firstSegment;
}

/**
 * Resolve active company slug with route-aware priority order:
 * 1. URL slug (if present on company route) - ALWAYS takes precedence
 * 2. localStorage v2 (ONLY if on company route, not superadmin/auth/public)
 * 3. User's default company slug (from auth)
 * 4. Environment-based default (only "test" in local/dev, never in production)
 * 
 * CRITICAL: On superadmin/auth/public routes, returns empty string and ignores localStorage
 * 
 * @param options - Resolution options
 * @param options.authUserSlug - User's default company slug from backend/auth
 * @param options.env - Override environment detection (optional)
 * @returns Resolved company slug, or empty string if on non-company route
 */
export function resolveActiveCompanySlug(options: {
  authUserSlug?: string | null;
  env?: 'local' | 'staging' | 'production';
} = {}): string {
  const { authUserSlug, env } = options;
  const runtimeEnv = env || getRuntimeEnvFromHostname();
  
  // Get current pathname to determine route type
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  
  // CRITICAL: If on superadmin or auth/public route, do NOT use localStorage
  // Return empty string immediately (caller should handle undefined/empty slug)
  if (isSuperadminRoute(pathname) || isAuthOrPublicPath(pathname)) {
    // Clean up any stale slug from localStorage when on non-company routes
    // CRITICAL: Never store "superadmin" as a company slug - remove it if found
    if (typeof window !== 'undefined') {
      try {
        const SLUG_STORAGE_KEY = 'wjs_active_company_v2';
        const storedSlug = localStorage.getItem(SLUG_STORAGE_KEY);
        if (storedSlug) {
          // If stored slug is "superadmin" or we're on superadmin route, clear it
          if (storedSlug === 'superadmin' || isSuperadminRoute(pathname)) {
            localStorage.removeItem(SLUG_STORAGE_KEY);
          }
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    return '';
  }

  // PRIORITY 1: URL slug is ALWAYS the source of truth (on company routes)
  const slugFromUrl = getCompanySlugFromLocation();
  if (slugFromUrl) {
    // CRITICAL: Never store "superadmin" as a company slug
    if (slugFromUrl === 'superadmin' || slugFromUrl.toLowerCase() === 'superadmin') {
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('wjs_active_company_v2');
        } catch (e) {
          // Ignore localStorage errors
        }
      }
      return '';
    }
    
    // Only save to localStorage if we're on a company route
    if (typeof window !== 'undefined' && isCompanyRoute(pathname)) {
      try {
        localStorage.setItem('wjs_active_company_v2', slugFromUrl);
      } catch (e) {
        // Failed to save to localStorage
      }
    }
    return slugFromUrl;
  }

  // PRIORITY 2: Check localStorage v2 (ONLY if on company route)
  // CRITICAL: Only use localStorage if we're on a company route
  if (typeof window !== 'undefined' && isCompanyRoute(pathname)) {
    try {
      const storedSlug = localStorage.getItem('wjs_active_company_v2');
      if (storedSlug && 
          storedSlug !== 'undefined' && 
          storedSlug !== 'null' && 
          storedSlug.trim() !== '') {
        // CRITICAL: Never use "superadmin" as a company slug - clear it if found
        if (storedSlug === 'superadmin' || storedSlug.toLowerCase() === 'superadmin') {
          localStorage.removeItem('wjs_active_company_v2');
          // Continue to next priority (user default or env default)
        } else {
          // Validate format
          if (/^[a-zA-Z0-9_-]+$/.test(storedSlug) && 
              storedSlug.length >= 1 && 
              storedSlug.length <= 100) {
            return storedSlug;
          }
        }
      }
    } catch (e) {
      // Failed to read localStorage
    }
  }

  // PRIORITY 3: User's default company slug from auth
  // Only use if we're on a company route (or if pathname is empty/unknown)
  if (authUserSlug && 
      authUserSlug !== 'undefined' && 
      authUserSlug !== 'null' && 
      authUserSlug.trim() !== '') {
    // Validate format
    if (/^[a-zA-Z0-9_-]+$/.test(authUserSlug) && 
        authUserSlug.length >= 1 && 
        authUserSlug.length <= 100) {
      // CRITICAL: Never store "superadmin" as a company slug
      if (authUserSlug === 'superadmin' || authUserSlug.toLowerCase() === 'superadmin') {
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('wjs_active_company_v2');
          } catch (e) {
            // Ignore localStorage errors
          }
        }
        return '';
      }
      
      // Only save to localStorage if we're on a company route
      if (typeof window !== 'undefined' && isCompanyRoute(pathname)) {
        try {
          localStorage.setItem('wjs_active_company_v2', authUserSlug);
        } catch (e) {
          // Failed to save to localStorage
        }
      }
      return authUserSlug;
    }
  }

  // PRIORITY 4: Environment-based default
  // CRITICAL: "test" is ONLY allowed in local/dev environments
  // AND only if we're on a company route (not superadmin/auth/public)
  if (isCompanyRoute(pathname) && (runtimeEnv === 'local' || runtimeEnv === 'staging')) {
    return 'test';
  }

  // PRODUCTION or non-company route: Return empty string
  // This prevents silent redirects to "test" in production
  return '';
}

/**
 * Get user's default company slug from stored auth data
 * 
 * Extracts company slug from localStorage client data or token payload
 * 
 * @returns User's default company slug, or null if not found
 */
export function getUserDefaultCompanySlug(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Try to get from client data
    const clientStr = localStorage.getItem('client');
    if (clientStr) {
      const client = JSON.parse(clientStr);
      // Check various possible fields
      const companySlug = client?.company?.slug || 
                          client?.company || 
                          client?.companySlug ||
                          client?.defaultCompanySlug;
      if (companySlug && 
          typeof companySlug === 'string' && 
          companySlug !== 'undefined' && 
          companySlug !== 'null' &&
          companySlug !== 'superadmin' &&
          companySlug.toLowerCase() !== 'superadmin') {
        return companySlug;
      }
    }

    // Try to get from token payload
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        const companySlug = payload?.company?.slug || 
                            payload?.company || 
                            payload?.companySlug ||
                            payload?.defaultCompanySlug;
        if (companySlug && 
            typeof companySlug === 'string' && 
            companySlug !== 'undefined' && 
            companySlug !== 'null' &&
            companySlug !== 'superadmin' &&
            companySlug.toLowerCase() !== 'superadmin') {
          return companySlug;
      }
    } catch (e) {
      // Invalid token format - ignore
      }
    }
  } catch (e) {
    // Error extracting user default company slug
  }

  return null;
}

/**
 * Convenience function that combines getUserDefaultCompanySlug with resolveActiveCompanySlug
 * 
 * This is the main function to use when you need the active company slug.
 * It automatically extracts user's default company from auth data.
 * 
 * @param options - Optional override for environment
 * @returns Resolved company slug (empty string on non-company routes)
 */
export function getActiveCompanySlug(options: {
  env?: 'local' | 'staging' | 'production';
} = {}): string {
  const userSlug = getUserDefaultCompanySlug();
  return resolveActiveCompanySlug({
    authUserSlug: userSlug || undefined,
    env: options.env,
  });
}

/**
 * Clear company slug from localStorage
 * 
 * Use this when:
 * - User logs out
 * - User switches to superadmin
 * - User navigates to public/auth routes
 * 
 * CRITICAL: Also removes "superadmin" if it was incorrectly stored as a company slug
 * 
 * @returns true if slug was cleared, false otherwise
 */
export function clearCompanySlug(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  try {
    const SLUG_STORAGE_KEY = 'wjs_active_company_v2';
    const storedSlug = localStorage.getItem(SLUG_STORAGE_KEY);
    const hadSlug = storedSlug !== null;
    
    // Always remove, especially if it's "superadmin" (should never be stored)
    if (hadSlug) {
      localStorage.removeItem(SLUG_STORAGE_KEY);
    }
    return hadSlug;
  } catch (e) {
    // Failed to clear localStorage
    return false;
  }
}

